import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { AUTH_F } from '@/constants/testIds/healthspan';
import { toast } from 'sonner';

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminSecret, setAdminSecret] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    setBusy(true);
    try {
      const u = await register(email.trim(), password, adminSecret || undefined);
      toast.success(`Welcome — ${u.role} account ready.`);
      nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <Card className="p-6 sm:p-8 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
        <h1 className="text-xl font-semibold tracking-tight">Create your account</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Free tier includes biomarker matching and protocol catalogue.</p>
        <form data-testid={AUTH_F.registerForm} onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="r-email">Email</Label>
            <Input data-testid={AUTH_F.registerEmail} id="r-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="r-password">Password</Label>
            <Input data-testid={AUTH_F.registerPassword} id="r-password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} />
            <p className="text-[11px] text-[color:var(--muted-fg)]">Minimum 6 characters.</p>
          </div>
          <button type="button" onClick={()=>setShowAdmin(s=>!s)} className="text-xs text-[color:var(--muted-fg)] hover:underline">{showAdmin ? 'Hide' : 'Show'} admin enrolment</button>
          {showAdmin && (
            <div className="space-y-1">
              <Label htmlFor="r-admin">Admin secret (optional)</Label>
              <Input data-testid={AUTH_F.adminSecret} id="r-admin" type="text" value={adminSecret} onChange={(e)=>setAdminSecret(e.target.value)} placeholder="Leave empty for normal account" />
              <p className="text-[11px] text-[color:var(--muted-fg)]">Set in backend .env as JWT_SECRET + '_admin'. Only the operator should know this.</p>
            </div>
          )}
          <Button data-testid={AUTH_F.registerSubmit} type="submit" disabled={busy} className="w-full bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">{busy ? 'Creating…' : 'Create account'}</Button>
        </form>
        <p className="mt-4 text-sm text-[color:var(--muted-fg)]">Already have an account? <Link to="/login" className="underline underline-offset-4">Sign in</Link></p>
      </Card>
    </div>
  );
}
